import React, { useMemo } from 'react';
import { css } from '@emotion/react';
import { Grid, StylesProvider } from '@material-ui/core';
import { calcDefaultServiceTime } from '@/utilities/calculateTimeframes';
import { integerFormatter } from '@/constants/formats';
import { Body1, Body2 } from '../blocks';
import { useItemQuantity, useTotalCubes, useOrderCarrierPricing, useTotalRevenue } from '@/hooks';
import { LOCATION_TYPES } from '@/constants/locationTypes';
import deliveryStrings from '@/constants/deliveryStrings';
import { capitalize, startCase } from 'lodash';
import { genAttributes, MARKETPLACE_TIERS } from '@onward-delivery/core';

// Keep in sync with the MARKETPLACE_TYPE filter in DispatchPlan/constants.js.
const MARKETPLACE_TIER_LIST = [MARKETPLACE_TIERS.T3_FULLY_MANAGED, MARKETPLACE_TIERS.T2_INTEGRATIONS_BILLING];
import { toNational } from '@/utilities/formatPhoneNumber';
import { colors } from '@/styles';
import { calculateDwellDays } from '@/utilities/calculateDwellDays';
import { toFullState } from '@/utilities/address';
import { PRESCHEDULED_HIGHLIGHT } from '@/constants/featureFlags';

const Metric = ({ label, value }) => {
    const render = [...(label ? [label] : []), value];
    return (
        <Grid item>
            <Body2>{render.join(': ')}</Body2>
        </Grid>
    );
};

const BoldMetric = ({ label, value }) => {
    const render = [...(label ? [label] : []), value];
    return (
        <Grid item>
            <Body1>{render.join(': ')}</Body1>
        </Grid>
    );
};

const DwellMetric = ({ label, value }) => {
    const render = [...(label ? [label] : []), value];
    return (
        <Grid item>
            <Body1
                style={{
                    color: colors.reds.primary,
                }}
            >
                {render.join(': ')}
            </Body1>
        </Grid>
    );
};

const Badge = ({ label, backgroundColor, textColor }) => {
    return (
        <Grid
            item
            style={{
                padding: '0 4px',
                marginRight: '4px',
                backgroundColor,
                color: textColor,
            }}
        >
            <Body1>{label}</Body1>
        </Grid>
    );
};

const OrderTooltip = ({ order, stop, user_id, dispatch_settings, invoiceAccess, circles }) => {
    const totalItems = useItemQuantity([order]);
    const totalVolume = useTotalCubes([order]);
    const isShipper = user_id === order.shipper_id;
    const rate = order.oms ? order.order_revenue : isShipper ? order.shipper_rate : order.carrier_rate;

    const serviceTime = useMemo(() => {
        if (!stop) {
            return null;
        }

        if (stop.service_time === 0 || stop.service_time) {
            return stop.service_time / 1000 / 60;
        }

        const { hours, minutes } = calcDefaultServiceTime(stop, { [order.order_id]: order }, dispatch_settings);
        return (hours || 0) * 60 + (minutes || 0);
    }, [stop, order]);

    const badges = useMemo(() => {
        let badges = [];
        const isMarketplace =
            !order.oms && MARKETPLACE_TIER_LIST.includes(order?.shipper_rate_override?.marketplace_tier);
        if (isMarketplace) {
            badges.push({
                key: 'marketplace',
                label: 'Marketplace Order',
                backgroundColor: colors.greens.primary,
                textColor: 'white',
            });
        }
        if (order.prescheduled && circles?.[PRESCHEDULED_HIGHLIGHT]) {
            badges.push({
                key: 'prescheduled',
                label: 'Prescheduled',
                backgroundColor: colors.golds.primary,
                textColor: 'white',
            });
        }
        if (!order.delivery_date) {
            badges.push({
                key: 'unscheduled',
                label: 'Unscheduled Order',
                backgroundColor: colors.reds.primary,
                textColor: 'white',
            });
        } else {
            badges.push({
                key: 'scheduled',
                // delivery_date_formatted (cache.js) reads the date in the order's
                // timezone, matching Order Details. Formatting the raw timestamptz
                // here would shift midnight-UTC dates back a day in US browsers.
                label: `Scheduled: ${order.delivery_date_formatted}`,
                backgroundColor: colors.golds.primary,
                textColor: 'white',
            });
        }
        return badges;
    }, [order, circles]);

    const dwellDays = useMemo(() => calculateDwellDays(order), [order]);

    const { street, unit, city, state, zip, location_type } = genAttributes(order);

    const items = useMemo(() => {
        return (order.itemsByOrderId || []).reduce((acc, item) => {
            let lines = [];
            const qty = item.quantity || 1;
            for (let i = 0; i < qty; i++) {
                lines.push(item);
            }
            return [...acc, ...lines];
        }, []);
    }, [order]);

    const surveys = useMemo(() => {
        return (order.survey_responses || []).filter((response) =>
            ['PREDELIVERY', 'PREDELIVERY_DEFAULTS'].includes(response.survey_question.survey_type)
        );
    }, [order]);

    const getResponse = (response, question) => {
        switch (question.response_type) {
            case 'string':
                return response.response_string;
            case 'boolean':
                return response.response_boolean ? 'Yes' : 'No';
            case 'number':
                return `${(response.response_number || 0).toFixed(1)}`;
        }
        return null;
    };

    const tags = useMemo(() => {
        return (order.tags || []).filter(({ tag }) => tag.route_tag).map(({ tag }) => tag.tag);
    }, [order]);

    const dropoffNotes = useMemo(() => {
        return (order.notes || []).filter((note) => note.type === 'Delivery' && !note.private_to);
    }, [order]);

    const pinLabels = useMemo(() => {
        return (order.pin_labels || []).map((label) => String(label ?? '').trim()).filter(Boolean);
    }, [order]);

    return (
        <>
            {badges.map(({ key, ...badge }) => (
                <Badge key={key} {...badge} />
            ))}
            {dwellDays > 1 && <DwellMetric value={`Dwell: ${dwellDays} days`} />}
            <BoldMetric value={`${order.dropoff_name} - ${order.po_number}`} />
            {order.reference_id && <BoldMetric value={order.reference_id} />}
            {invoiceAccess && rate ? <Metric label={'Revenue'} value={`$${(rate || 0).toFixed(2)}`} /> : null}
            <Metric value={[order[street], order[unit]].filter((x) => x).join(', ')} />
            <Metric value={[order[city], toFullState(order[state]), order[zip]].filter((x) => x).join(', ')} />
            <Metric label="Phone" value={toNational(order.dropoff_phone)} />
            <Metric label="Order Type" value={startCase(order.order_type)} />
            <Metric
                label="Service Type"
                value={order.service_level?.service_level || LOCATION_TYPES[order[location_type]] || '-'}
            />
            {serviceTime && <Metric label="Service Time" value={`${serviceTime} min`} />}
            <Metric
                value={[
                    `${integerFormatter.format(totalItems)} items`,
                    `${integerFormatter.format(totalVolume)} cu ft.`,
                ].join(' / ')}
            />
            {items.map((item, idx) => (
                <Metric
                    key={`${item.item_id}-${idx}`}
                    value={[item.sku, item.description].filter((x) => !!x).join(' - ')}
                />
            ))}
            {tags.length > 0 ? <Metric label="Tags" value={tags.join(', ')} /> : null}
            {/* Always listed, whether or not the pin's box had room for them.
                The box shows the union across every order on the pin, so this
                is the only place that says which labels belong to which order. */}
            {pinLabels.length > 0 ? <Metric label="Pin Labels" value={pinLabels.join(', ')} /> : null}
            {/* {surveys.map((response, idx) => (
                <Metric
                    key={response.survey_id}
                    label={`${idx + 1}) ${response.survey_question.question}`}
                    value={getResponse(response, response.survey_question)}
                />
            ))} */}
            {surveys.length > 0 ? (
                <Metric
                    label="Survey Responses"
                    value={surveys
                        .map((response, idx) =>
                            [
                                `${idx + 1}) ${response.survey_question.question}`,
                                getResponse(response, response.survey_question),
                            ].join(': ')
                        )
                        .join(' ')}
                />
            ) : null}
            {order.notes?.filter((note) => note.type === 'Delivery').length > 0 && (
                <Metric
                    label="Delivery Notes"
                    value={order.notes
                        .filter((note) => note.type === 'Delivery')
                        .map((note) => note.note)
                        .join(', ')}
                />
            )}
        </>
    );
};

const MapStopTooltip = ({ orders, stop, user_id, dispatch_settings, invoiceAccess, circles }) => {
    if (!orders?.length) return <Body1>Error! Missing Order Information</Body1>;

    return (
        <StylesProvider injectFirst>
            <Grid container direction="column">
                {orders
                    .filter((o) => !!o)
                    .map((order) => (
                        <OrderTooltip
                            // Without a key React reconciles these rows by
                            // index, so hovering a pin with a different order
                            // count can carry one order's rows onto another.
                            key={`${order.order_id}_${order.crossdock_leg}`}
                            order={order}
                            stop={stop}
                            user_id={user_id}
                            dispatch_settings={dispatch_settings}
                            invoiceAccess={invoiceAccess}
                            circles={circles}
                        />
                    ))}
            </Grid>
        </StylesProvider>
    );
};

export default MapStopTooltip;
